/**
 * Reglas de negocio puras del Reporte Diario (RD) — VD §4.14 + §6.7.
 * Funciones puras testeables sin UI ni backend.
 */

import type {
  Rd,
  Jornada,
  Recursos,
  RecursoMaquinaria,
  RecursoVehiculo,
  RecursoPersonal,
  Tarea,
  AvanceTarea,
} from "@app-types/entities";
import type { EstadoRd } from "@constants/estados";
import { TRANSICIONES_RD, puedeTransicionar } from "@constants/estados";
import {
  DIESEL_MAQ_AMARILLA,
  DIESEL_MAQ_HERRAMIENTAS,
  DIESEL_VEH_MOVILIZACION,
  DIESEL_VEH_CARGA,
  CO2_PER_GAL_DIESEL,
} from "@constants/diesel";

// ============================================================================
// Jornada
// ============================================================================

export function calcularJornadaEfectiva(j: Jornada): number {
  const [hI, mI] = j.horaInicio.split(":").map(Number);
  const [hF, mF] = j.horaFin.split(":").map(Number);
  if (hI === undefined || mI === undefined || hF === undefined || mF === undefined) return 0;
  const minutosTrabajados = hF * 60 + mF - (hI * 60 + mI) - j.descansosMinutos;
  const horasTrabajadas = minutosTrabajados / 60;
  const efectiva = horasTrabajadas - j.horasLluvia - j.horasTormenta;
  return Math.max(0, Math.round(efectiva * 10) / 10);
}

export function validarJornada(j: Jornada): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!/^\d{2}:\d{2}$/.test(j.horaInicio)) errores.push("Hora inicio inválida (formato HH:MM)");
  if (!/^\d{2}:\d{2}$/.test(j.horaFin)) errores.push("Hora fin inválida (formato HH:MM)");
  if (j.horasLluvia < 0) errores.push("Horas lluvia no puede ser negativa");
  if (j.horasTormenta < 0) errores.push("Horas tormenta no puede ser negativa");
  if (j.descansosMinutos < 0) errores.push("Descansos no pueden ser negativos");
  return { valido: errores.length === 0, errores };
}

// ============================================================================
// Recursos: galones y CO2
// ============================================================================

export function galonesMaqAmarilla(recursos: RecursoMaquinaria[]): number {
  return recursos.reduce((sum, r) => {
    const gph = DIESEL_MAQ_AMARILLA[r.equipo] ?? 0;
    return sum + r.cantidad * r.horas * gph;
  }, 0);
}

export function galonesMaqHerramientas(recursos: RecursoMaquinaria[]): number {
  return recursos.reduce((sum, r) => {
    const gph = DIESEL_MAQ_HERRAMIENTAS[r.equipo] ?? 0;
    return sum + r.cantidad * r.horas * gph;
  }, 0);
}

export function galonesVehMovilizacion(recursos: RecursoVehiculo[]): number {
  return recursos.reduce((sum, r) => {
    const kmpg = DIESEL_VEH_MOVILIZACION[r.equipo] ?? 0;
    if (kmpg === 0) return sum;
    return sum + (r.cantidad * r.km) / kmpg;
  }, 0);
}

export function galonesVehCarga(recursos: RecursoVehiculo[]): number {
  return recursos.reduce((sum, r) => {
    const kmpg = DIESEL_VEH_CARGA[r.equipo] ?? 0;
    if (kmpg === 0) return sum;
    return sum + (r.cantidad * r.km) / kmpg;
  }, 0);
}

export function totalGalonesRd(recursos: Recursos): number {
  return (
    galonesMaqAmarilla(recursos.maqAmarilla) +
    galonesMaqHerramientas(recursos.maqHerramientas) +
    galonesVehMovilizacion(recursos.vehMovilizacion) +
    galonesVehCarga(recursos.vehCarga)
  );
}

export function totalCO2Rd(recursos: Recursos): number {
  return totalGalonesRd(recursos) * CO2_PER_GAL_DIESEL;
}

/** Horas Maquinaria Trabajadas: solo categorías gal/h. */
export function totalHmt(recursos: Recursos): number {
  const hmtAmarilla = recursos.maqAmarilla.reduce((s, r) => s + r.cantidad * r.horas, 0);
  const hmtHerramientas = recursos.maqHerramientas.reduce((s, r) => s + r.cantidad * r.horas, 0);
  return Math.round((hmtAmarilla + hmtHerramientas) * 10) / 10;
}

// ============================================================================
// Personal
// ============================================================================

export function totalPersonal(personal: RecursoPersonal[]): number {
  return personal.reduce((s, p) => s + p.cantidad, 0);
}

export function porcentajePersonalLocal(personal: RecursoPersonal[]): number {
  const total = totalPersonal(personal);
  if (total === 0) return 0;
  const local = personal.filter((p) => p.origen === "Local").reduce((s, p) => s + p.cantidad, 0);
  return Math.round((local / total) * 1000) / 10;
}

export function totalPorClase(
  personal: RecursoPersonal[],
): { staff: number; moc: number; monc: number } {
  return {
    staff: personal.filter((p) => p.clase === "STAFF").reduce((s, p) => s + p.cantidad, 0),
    moc: personal.filter((p) => p.clase === "MOC").reduce((s, p) => s + p.cantidad, 0),
    monc: personal.filter((p) => p.clase === "MONC").reduce((s, p) => s + p.cantidad, 0),
  };
}

// ============================================================================
// Estados (modelo simplificado VD §5.6 — 3 estados + sub-flag)
// ============================================================================

export function puedeTransicionarRd(actual: EstadoRd, destino: EstadoRd): boolean {
  return puedeTransicionar(actual, destino, TRANSICIONES_RD);
}

export function esEstadoFinal(estado: EstadoRd): boolean {
  return estado === "APROBADO" || estado === "CANCELADO";
}

/** Contratista solo edita en BORRADOR. Tras envío queda read-only. */
export function esEditablePorContratista(estado: EstadoRd): boolean {
  return estado === "BORRADOR";
}

/**
 * INT deja su CONCEPTO (calificación 1-5 + observaciones) mientras el RD esté
 * en revisión. Es consultivo: no bloquea ni habilita al PL2 (revisión en
 * paralelo — flujo canónico pt. 10). El semáforo del PL2 indica si ya lo dejó.
 */
export function esRevisablePorInt(estado: EstadoRd): boolean {
  return estado === "EN_REVISION";
}

/**
 * PL/PL2 puede aprobar o rechazar CUALQUIER RD en revisión, haya o no concepto
 * del INT (flujo canónico pt. 10). Único aprobador del RD.
 */
export function esRevisablePorPl(estado: EstadoRd): boolean {
  return estado === "EN_REVISION";
}

// ============================================================================
// Orden cronológico de aprobación — regla del responsable del PMIS
// ============================================================================

/**
 * Reportes de la MISMA OT, con fecha estrictamente anterior, que todavía no
 * están decididos.
 *
 * Regla del responsable: *"si hay reportes diarios no aprobados, pendientes,
 * no se pueden aprobar reportes subsiguientes hasta completar las aprobaciones
 * de los RD previos"*. Blinda la ambigüedad en las cantidades de obra que
 * alimentan la certificación: con esto, el conjunto de RD aprobados de una OT
 * es siempre un PREFIJO de la serie, sin huecos por debajo del último
 * aprobado. Eso es lo que convierte "hasta el último reporte aprobado" en una
 * frontera hermética y no en una aproximación.
 *
 * Detalles que no son arbitrarios:
 *  - Comparación de fechas como texto: `rd.fecha` es "YYYY-MM-DD", cuyo orden
 *    lexicográfico coincide con el cronológico. Es lo que ya hacen `listBySo`
 *    y `generarLineasActa`.
 *  - El `<` es ESTRICTO. La unicidad de (soId, fecha) la sostiene la
 *    aplicación, no la base — no hay índice único —, así que con `<=` un
 *    duplicado se bloquearía a sí mismo para siempre.
 *  - CANCELADO no bloquea: es terminal y es la válvula de escape.
 *  - BORRADOR SÍ bloquea. Es el estado inicial, el de rechazo y el de
 *    re-edición, y en los tres casos contiene cantidades que tarde o temprano
 *    se aprobarán. Por eso el borrador huérfano tiene que ser visible y
 *    tener salida (ver RdListPage y la válvula del PL2).
 *  - Un estado desconocido —dato migrado o semilla antigua— cuenta como
 *    bloqueante: es el fallo seguro correcto.
 *  - La regla es POR OT: dos OT del mismo contratista son independientes,
 *    porque el acta se certifica por OT.
 */
export function rdsPreviosBloqueantes(
  rd: Pick<Rd, "soId" | "fecha">,
  rdsDeLaOt: Pick<Rd, "id" | "soId" | "fecha" | "estado">[],
): Pick<Rd, "id" | "fecha" | "estado">[] {
  return rdsDeLaOt
    .filter(
      (r) =>
        r.soId === rd.soId &&
        r.fecha < rd.fecha &&
        r.estado !== "APROBADO" &&
        r.estado !== "CANCELADO",
    )
    .map((r) => ({ id: r.id, fecha: r.fecha, estado: r.estado }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
}

/**
 * Decisión sobre el orden. Devuelve la lista completa de bloqueantes para que
 * la interfaz pueda nombrar el más antiguo y enlazarlo, y para que el
 * repositorio componga el mensaje del error.
 */
export function puedeAprobarRdPorOrden(
  rd: Pick<Rd, "soId" | "fecha">,
  rdsDeLaOt: Pick<Rd, "id" | "soId" | "fecha" | "estado">[],
): { puede: boolean; bloqueantes: Pick<Rd, "id" | "fecha" | "estado">[] } {
  const bloqueantes = rdsPreviosBloqueantes(rd, rdsDeLaOt);
  return { puede: bloqueantes.length === 0, bloqueantes };
}

/** Mensaje único para la interfaz y para el repositorio. */
export function mensajeBloqueoPorOrden(
  bloqueantes: Pick<Rd, "fecha" | "estado">[],
): string {
  const primero = bloqueantes[0];
  if (!primero) return "";
  const extra =
    bloqueantes.length > 1 ? ` (y ${bloqueantes.length - 1} más)` : "";
  return (
    `No se puede aprobar: el reporte del ${primero.fecha} de esta OT sigue ` +
    `en ${primero.estado}${extra}. Los reportes se aprueban en orden ` +
    `cronológico — decida primero el más antiguo.`
  );
}

// ============================================================================
// Validación completa del RD
// ============================================================================

export function validarRd(rd: Rd): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!rd.soId) errores.push("Orden de Trabajo obligatoria");
  if (!rd.fecha) errores.push("Fecha obligatoria");
  const j = validarJornada(rd.jornada);
  if (!j.valido) errores.push(...j.errores);
  if (rd.avancesTareas.length === 0) {
    errores.push("Debe registrar al menos el avance de una tarea");
  }
  return { valido: errores.length === 0, errores };
}

// ============================================================================
// Alertas §6.7.6 — umbrales parametrizables §F4
// ============================================================================

/**
 * Umbrales por defecto (parametrizables a nivel global por el JEFE/ADMIN_PMIS
 * desde el módulo de configuración del sistema — decisión §F4).
 *
 * Cuando se construya el módulo de parametrización, leer estos valores de
 * localStorage o de una lista SharePoint dedicada y mantener los defaults
 * como fallback.
 */
export const UMBRALES_RD_DEFAULT = {
  /** Aviso preventivo cuando una tarea alcanza este % del presupuesto. */
  alertaAvanceTareaPct: 90,
  /** Bloqueo de registro adicional cuando una tarea alcanza este %. */
  bloqueoAvanceTareaPct: 105,
  /** Atraso porcentual mínimo para mostrar advertencia de desviación. */
  atrasoTareaPct: 25,
  /** Aviso global cuando avance total del proyecto alcanza este %. */
  alertaAvanceTotalPct: 90,
  /** Bloqueo total cuando avance del proyecto alcanza este %. */
  bloqueoAvanceTotalPct: 100,
  /** SPI mínimo aceptable evaluado al avance de proyecto = 50%. */
  spiMinimo: 0.8,
  /** Punto del proyecto donde se evalúa el SPI mínimo. */
  pctEvaluacionSpi: 50,
};

export type UmbralesRd = typeof UMBRALES_RD_DEFAULT;

const UMBRALES_KEY = "pmis.umbralesRd";

/** Lee umbrales actuales (override de localStorage o default). */
export function leerUmbralesRd(): UmbralesRd {
  if (typeof window === "undefined") return UMBRALES_RD_DEFAULT;
  try {
    const raw = window.localStorage.getItem(UMBRALES_KEY);
    if (!raw) return UMBRALES_RD_DEFAULT;
    const parsed = JSON.parse(raw) as Partial<UmbralesRd>;
    return { ...UMBRALES_RD_DEFAULT, ...parsed };
  } catch {
    return UMBRALES_RD_DEFAULT;
  }
}

/** Guarda umbrales (solo el ADMIN_PMIS / JEFE debería llamar esto). */
export function guardarUmbralesRd(u: Partial<UmbralesRd>): void {
  if (typeof window === "undefined") return;
  const merged = { ...leerUmbralesRd(), ...u };
  window.localStorage.setItem(UMBRALES_KEY, JSON.stringify(merged));
}

// ============================================================================
// Validación de avance contra el PDT y alertas §6.7.6
// ============================================================================

export type SeveridadAlerta = "info" | "warning" | "error";

export interface AlertaAvance {
  tareaId: string;
  codigo: string;
  severidad: SeveridadAlerta;
  mensaje: string;
}

/**
 * Evalúa el avance reportado en el RD contra el PDT (tareas) y produce
 * alertas según los umbrales §F4 §6.7.6:
 *
 *   - Tarea ≥ 90% → info (aviso preventivo)
 *   - Tarea ≥ 105% → error (bloquea registro adicional)
 *   - Tarea atrasada > 25% → warning (recomienda recuperar)
 */
export function alertasAvance(
  avancesAcumuladosPorTarea: Map<string, number>, // tareaId → % acumulado al cerrar este RD
  tareas: Pick<Tarea, "id" | "codigo" | "fechaInicio" | "fechaFin">[],
  umbrales: UmbralesRd = leerUmbralesRd(),
  hoy: Date = new Date(),
): AlertaAvance[] {
  const alertas: AlertaAvance[] = [];
  const hoyIso = hoy.toISOString().slice(0, 10);
  const ts = (s: string) => new Date(s + "T00:00:00").getTime();

  for (const t of tareas) {
    const avance = avancesAcumuladosPorTarea.get(t.id) ?? 0;

    // 1) Sobre-ejecución
    if (avance >= umbrales.bloqueoAvanceTareaPct) {
      alertas.push({
        tareaId: t.id,
        codigo: t.codigo,
        severidad: "error",
        mensaje: `Tarea ${t.codigo} al ${avance.toFixed(1)}% — bloqueada para registro adicional (umbral ${umbrales.bloqueoAvanceTareaPct}%).`,
      });
    } else if (avance >= umbrales.alertaAvanceTareaPct) {
      alertas.push({
        tareaId: t.id,
        codigo: t.codigo,
        severidad: "info",
        mensaje: `Tarea ${t.codigo} al ${avance.toFixed(1)}% — aproximándose al cierre.`,
      });
    }

    // 2) Atraso vs cronograma planeado
    const tIni = ts(t.fechaInicio);
    const tFin = ts(t.fechaFin);
    const ahora = ts(hoyIso);
    if (tFin > tIni && ahora >= tIni) {
      const planeado = Math.min(100, ((ahora - tIni) / (tFin - tIni)) * 100);
      const atraso = planeado - avance;
      if (atraso > umbrales.atrasoTareaPct) {
        alertas.push({
          tareaId: t.id,
          codigo: t.codigo,
          severidad: "warning",
          mensaje: `Tarea ${t.codigo} con atraso ${atraso.toFixed(1)} pp (avance ${avance.toFixed(1)}% vs planeado ${planeado.toFixed(1)}%).`,
        });
      }
    }
  }

  return alertas;
}

/**
 * Alertas globales del proyecto (avance total y SPI).
 *
 *   - Avance total ≥ 90% → info
 *   - Avance total ≥ 100% → error (bloquea registro)
 *   - SPI ≤ 0.8 al alcanzar el 50% del plan → warning
 */
export function alertasProyecto(args: {
  avanceTotalPct: number;
  spi: number;
  pctTiempoTranscurrido: number;
  umbrales?: UmbralesRd;
}): AlertaAvance[] {
  const u = args.umbrales ?? leerUmbralesRd();
  const alertas: AlertaAvance[] = [];

  if (args.avanceTotalPct >= u.bloqueoAvanceTotalPct) {
    alertas.push({
      tareaId: "__GLOBAL__",
      codigo: "PROYECTO",
      severidad: "error",
      mensaje: `Avance total ${args.avanceTotalPct.toFixed(1)}% — registro bloqueado.`,
    });
  } else if (args.avanceTotalPct >= u.alertaAvanceTotalPct) {
    alertas.push({
      tareaId: "__GLOBAL__",
      codigo: "PROYECTO",
      severidad: "info",
      mensaje: `Avance total ${args.avanceTotalPct.toFixed(1)}% — evaluar pendientes para cierre.`,
    });
  }

  if (
    args.pctTiempoTranscurrido >= u.pctEvaluacionSpi &&
    args.spi > 0 &&
    args.spi <= u.spiMinimo
  ) {
    alertas.push({
      tareaId: "__GLOBAL__",
      codigo: "PROYECTO",
      severidad: "warning",
      mensaje: `SPI ${args.spi.toFixed(2)} al ${args.pctTiempoTranscurrido.toFixed(0)}% del plan — implementar plan de acción inmediato.`,
    });
  }

  return alertas;
}

/**
 * Calcula avance acumulado por tarea sumando todos los RDs aprobados (o el
 * RD que se está editando si pasamos su contenido).
 */
export function avanceAcumuladoPorTarea(
  rdsAprobados: Pick<Rd, "estado" | "avancesTareas">[],
  rdEditando?: Pick<Rd, "avancesTareas">,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const rd of rdsAprobados) {
    if (rd.estado !== "APROBADO") continue;
    for (const a of rd.avancesTareas) {
      map.set(a.tareaId, (map.get(a.tareaId) ?? 0) + a.porcentajeDia);
    }
  }
  if (rdEditando) {
    for (const a of rdEditando.avancesTareas) {
      map.set(a.tareaId, (map.get(a.tareaId) ?? 0) + a.porcentajeDia);
    }
  }
  return map;
}

/** Validación blanda: la suma de avances diarios no puede exceder 100% por tarea. */
export function validarAvancesContraPdt(
  avances: AvanceTarea[],
  acumuladoPrevio: Map<string, number>,
  umbrales: UmbralesRd = leerUmbralesRd(),
): string[] {
  const errores: string[] = [];
  for (const a of avances) {
    const previo = acumuladoPrevio.get(a.tareaId) ?? 0;
    const nuevoAcum = previo + a.porcentajeDia;
    if (nuevoAcum > umbrales.bloqueoAvanceTareaPct) {
      errores.push(
        `Tarea ${a.tareaId} acumularía ${nuevoAcum.toFixed(1)}% (sobre el límite de ${umbrales.bloqueoAvanceTareaPct}%).`,
      );
    }
  }
  return errores;
}

// ============================================================================
// KPIs del RD
// ============================================================================

export function calcularKpis(rd: Rd) {
  const galones = totalGalonesRd(rd.recursos);
  return {
    jornadaEfectiva: rd.jornada.jornadaEfectivaHoras,
    horasLluvia: rd.jornada.horasLluvia,
    galones: Math.round(galones * 10) / 10,
    co2: Math.round(totalCO2Rd(rd.recursos) * 1000) / 1000,
    hmt: totalHmt(rd.recursos),
    personalTotal: totalPersonal(rd.recursos.personal),
    porcLocal: porcentajePersonalLocal(rd.recursos.personal),
  };
}

/** KPIs agregados para listado de RDs. */
export function kpisRds(rds: Rd[]): {
  total: number;
  borrador: number;
  enRevisionInt: number;
  enRevisionPl: number;
  aprobados: number;
  cancelados: number;
} {
  return {
    total: rds.length,
    borrador: rds.filter((r) => r.estado === "BORRADOR").length,
    enRevisionInt: rds.filter(
      (r) => r.estado === "EN_REVISION" && (r.nivelRevision ?? "INT") === "INT",
    ).length,
    enRevisionPl: rds.filter((r) => r.estado === "EN_REVISION" && r.nivelRevision === "PL")
      .length,
    aprobados: rds.filter((r) => r.estado === "APROBADO").length,
    cancelados: rds.filter((r) => r.estado === "CANCELADO").length,
  };
}
